import { FormMessage, Loader } from "@/atoms";
import { IconUpload } from "@tabler/icons-react";
import { ComponentProps, useMemo } from "react";
import { type DropzoneOptions, useDropzone } from "react-dropzone";

import { ONE_MEGABYTE, cn } from "@oneleet/utils";

import { twMerge } from "tailwind-merge";

import "./NgFileDropzone.css";

const DEFAULT_MAX_FILE_SIZE = 20 * ONE_MEGABYTE;

type NgFileDropzoneProps = ComponentProps<"div"> & {
  isLoading?: boolean;
  dropzoneOptions?: DropzoneOptions;
};

/**
 * A dropzone component that uses react-dropzone.
 *
 * @see https://react-dropzone.js.org/
 *
 * @example
 * <NgFileDropzone
 *   dropzoneOptions={{
 *     accept: {
 *       "application/pdf": [".pdf"],
 *       "image/gif": [".gif"],
 *     },
 *     maxSize: ONE_MEGABYTE,
 *     onDrop: (acceptedFiles, rejectedFiles) => {
 *       console.log("accepted", acceptedFiles);
 *       console.log("rejected", rejectedFiles);
 *     },
 *   }}
 * />
 */
export const NgFileDropzone = ({
  // All the useDropzone options
  dropzoneOptions: {
    disabled = false,
    accept,
    maxSize = DEFAULT_MAX_FILE_SIZE,
    ...dropzoneOptions
  } = {},

  // All the component props
  className,
  isLoading = false,
  ...rest
}: NgFileDropzoneProps) => {
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept,
      maxSize,
      disabled,
      ...dropzoneOptions,
    });

  const errorMessages = useMemo(() => {
    let rejectionMessages: string[] = [];
    fileRejections.forEach((rejection) => {
      return rejection.errors.map((error) => {
        let message = `${rejection.file.name}: ${error.message}`;
        if (error.code === "too-many-files" && dropzoneOptions.maxFiles) {
          message = `You can only upload ${dropzoneOptions.maxFiles} file${
            dropzoneOptions.maxFiles > 1 ? "s" : ""
          }`;
        } else if (
          error.code === "file-invalid-type" &&
          accept &&
          Object.keys(accept).length > 0
        ) {
          const extensions = Object.values(accept).flat();
          const listFormat = new Intl.ListFormat("en", {
            style: "long",
            type: "conjunction",
          });
          const formattedExtensions = listFormat.format(extensions);
          message = `Invalid file type: allowed types are ${formattedExtensions}`;
        }
        rejectionMessages = [...new Set([...rejectionMessages, message])];
      });
    });
    return rejectionMessages;
  }, [fileRejections, dropzoneOptions, accept]);

  return (
    <>
      <div
        {...getRootProps()}
        {...rest}
        className={twMerge(
          "relative flex w-full cursor-pointer flex-col items-center gap-1 rounded-md border-[1.4px] border-dashed border-neutral-400 bg-neutral-50 p-4",
          isDragActive && "border-accent-700 bg-accent-100",
          disabled
            ? "disabled-children cursor-not-allowed"
            : "hover:border-accent-700 hover:bg-accent-100 duration-300 hover:duration-0",
          className,
        )}
      >
        <input {...getInputProps()} />
        <div
          className={cn("flex items-center gap-2", isLoading && "opacity-50")}
        >
          <IconUpload className="text-neutral-550 size-4" />
          <p className="text-ng-md font-ng-medium text-neutral-600">
            Drag and drop, or{" "}
            <span className="text-accent-800">Browse files...</span>
          </p>
        </div>
        <p className="text-neutral-550 text-ng-sm">
          Up to {maxSize / ONE_MEGABYTE} MB
        </p>
        {isLoading && (
          <Loader
            delayed
            id="loader"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            color="accent"
          />
        )}
      </div>
      {errorMessages.map((errorMessage) => (
        <FormMessage
          text={errorMessage}
          color="red"
          className="italic"
          key={errorMessage}
        />
      ))}
    </>
  );
};
NgFileDropzone.displayName = "NgFileDropzone";
